import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Sorting,
} from "devextreme-react/data-grid";
import { exportDataGrid } from "devextreme/excel_exporter";
import { baseUrl } from "../../hooks/useFetch";

interface PositionsSPTableProps {
  page?: string;
  cobDate: Date | null;
  compareDate: Date | null;
}

interface GridHandle {
  getInstance: () => any;
  getData: () => any[];
}

interface ColDef {
  dataField: string;
  caption: string;
  allowSorting?: boolean;
  allowFiltering?: boolean;
  width?: number;
}

const colsLabels: Record<string, string> = {
  mtm: "MTM",
  delta: "Delta (lots)",
  gamma: "Gamma (delta per currency change in price per UOM)",
  vega: "Vega (currency/1%)",
  theta: "Theta (currency/day)",
  rho: "Rho (currency/1%)",
};

const headerStyle: React.CSSProperties = {
  whiteSpace: "normal",
  lineHeight: "16px",
  textAlign: "center",
  fontSize: "14px",
};

const metricCols = ["vega", "theta", "rho"];

const orderNormal = [
  "Total",
  "EUA",
  "UKA",
  "TTF",
  "Soybean",
  "Soybean oil",
  "Soybean meal",
  "Gasoil",
  "USD/BRL",
];

const orderTotals = [
  "EUA",
  "Gasoil",
  "Soybean",
  "Soybean meal",
  "Soybean oil",
  "TTF",
  "UKA",
  "USD/BRL",
  "Grand Total",
];

function sortRowsByAssettype(rows: any[], page: string): any[] {
  const order = page === "totals" ? orderTotals : orderNormal;
  const index = Object.fromEntries(order.map((name, i) => [name, i]));
  return [...rows].sort((a, b) => {
    const ai = index[a.assettype] ?? Number.MAX_SAFE_INTEGER;
    const bi = index[b.assettype] ?? Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });
}

const PositionsSPTable = forwardRef<GridHandle, PositionsSPTableProps>(
  ({ page = "normal", cobDate, compareDate }, ref) => {
    const [mode, setMode] = useState<string>("current");
    const [cols, setCols] = useState<string[]>([]);
    const [data, setData] = useState<any[]>([]);

    const gridInstanceRef = useRef<any>(null);

    const handleGridInitialized = (e: any): void => {
      gridInstanceRef.current = e.component;
    };

    useImperativeHandle(ref, () => ({
      getInstance: () => gridInstanceRef.current,
      getData: () => data,
    }));

    const dxColumns = useMemo((): ColDef[] => {
      if (!cols) return [];

      let valueColumns = cols.map((c) => ({
        dataField: c,
        caption: colsLabels[c] || c,
      }));

      if (page !== "totals") {
        valueColumns = valueColumns.filter((col) => col.dataField !== "assettype");
      }

      const filtered =
        page === "totals"
          ? valueColumns.filter((col) => ["vega", "theta"].includes(col.dataField))
          : valueColumns;

      return [
        {
          dataField: "assettype",
          caption: "",
          allowSorting: false,
          allowFiltering: false,
          width: 300,
        },
        ...filtered,
      ];
    }, [cols, page]);

    function onCellPrepared(e: any): void {
      if (e.rowType === "header" || e.rowType === "data") {
        e.cellElement.style.textAlign = "center";
        e.cellElement.style.justifyContent = "center";
      }
      if (e.column && e.column.dataField === "assettype") {
        e.cellElement.style.borderRight = "2px solid #4e4e4e";
      }
    }

    const onRowPrepared = (e: any): void => {
      const rowEl =
        e.rowElement instanceof HTMLElement ? e.rowElement : e.rowElement[0];

      if (e.rowType === "data") {
        rowEl.style.height = "16px";
        rowEl.style.lineHeight = "13px";
        rowEl.querySelectorAll("td").forEach((td: HTMLElement) => {
          const cell = td;
          cell.style.paddingTop = "1px";
          cell.style.paddingBottom = "1px";
          cell.style.paddingLeft = "4px";
          cell.style.paddingRight = "4px";
          cell.style.fontSize = "12px";
        });
      }

      if (e.rowType === "header") {
        rowEl.style.height = "25px";
        rowEl.style.lineHeight = "24px";
        rowEl.querySelectorAll("td").forEach((td: HTMLElement) => {
          const cell = td;
          cell.style.paddingTop = "5px";
          cell.style.paddingBottom = "5px";
          cell.style.paddingLeft = "2px";
          cell.style.paddingRight = "2px";
          cell.style.fontSize = "11px";
        });
      }
    };

    const renderHeader = (label: string): React.ReactElement => {
      const firstSpace = label.indexOf(" ");
      if (firstSpace === -1) {
        return <div style={headerStyle}>{label}</div>;
      }
      const before = label.slice(0, firstSpace);
      const after = label.slice(firstSpace + 1);
      return (
        <div style={headerStyle}>
          {before}
          <br />
          {after}
        </div>
      );
    };

    function addTotalsRow(rows: any[]): any[] {
      if (!rows || rows.length === 0) return [];
      const total: Record<string, any> =
        page === "totals"
          ? { assettype: "Grand Total" }
          : { assettype: "Total" };
      metricCols.forEach((col) => {
        total[col] = rows.reduce(
          (sum: number, r: any) => sum + (Number(r[col]) || 0),
          0
        );
      });
      if (page === "totals") {
        return [...rows, total];
      }
      return [total, ...rows];
    }

    useEffect(() => {
      if (!cobDate) return;

      const ctrl = new AbortController();
      const prevCursor = document.body.style.cursor;
      document.body.style.cursor = "wait";

      const start = cobDate.toISOString().split("T")[0];
      const isDiff = compareDate && compareDate !== cobDate;

      let url: string;
      if (isDiff) {
        const end = compareDate!.toISOString().split("T")[0];
        url = `${baseUrl}/positions_sp_exposure_dff?cob_date_start=${start}&cob_date_end=${end}`;
      } else {
        const params = new URLSearchParams({ cob_date: start });
        url = `${baseUrl}/positions_sp_exposure?${params.toString()}`;
      }

      (async () => {
        try {
          const res = await fetch(url, { signal: ctrl.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const json = await res.json();

          if (json.cols) {
            setCols(json.cols);
          }

          const raw = Array.isArray(json) ? json : json.data;
          setData(raw);
        } catch (err: any) {
          if (err.name !== "AbortError") console.error(err);
        } finally {
          document.body.style.cursor = prevCursor;
        }
      })();

      return () => {
        ctrl.abort();
        document.body.style.cursor = prevCursor;
      };
    }, [cobDate, compareDate, baseUrl]);

    const gridData = useMemo(
      () => sortRowsByAssettype(data ?? [], page),
      [data, page]
    );

    return (
      <DataGrid
        style={{
          width: page === "totals" ? "45%" : "95%",
          margin: "0",
        }}
        dataSource={gridData ?? []}
        showBorders
        rowAlternationEnabled
        allowColumnResizing
        allowColumnReordering
        columnAutoWidth={false}
        wordWrapEnabled
        onCellPrepared={onCellPrepared}
        onRowPrepared={onRowPrepared}
        onInitialized={handleGridInitialized}
        keyExpr="assettype"
      >
        <Sorting mode="multiple" />
        <HeaderFilter visible />
        <Paging enabled={false} />

        {dxColumns.map((col, index) => {
          const headerLabel = colsLabels[col.dataField];
          const { ...colWithoutKey } = col;
          const columnKey = col.dataField
            ? `${col.dataField}-${index}`
            : `col-${index}`;

          const baseProps: any = {
            ...colWithoutKey,
            ...(headerLabel && {
              headerCellRender: () => renderHeader(headerLabel),
            }),
          };

          if (col.dataField === "assettype") {
            return <Column key={columnKey} {...baseProps} />;
          }

          return (
            <Column
              key={columnKey}
              {...baseProps}
              cellRender={({ value }: { value: any }) => {
                if (
                  value == null ||
                  value === "" ||
                  Number.isNaN(Number(value))
                ) {
                  return <span>{value ?? ""}</span>;
                }

                const n = Number(value);

                if (n < 0) {
                  const formatted = Math.abs(n).toLocaleString("en-GB");
                  return <span style={{ color: "red" }}>({formatted})</span>;
                }

                return <span>{n.toLocaleString("en-GB")}</span>;
              }}
            />
          );
        })}
      </DataGrid>
    );
  }
);

export default PositionsSPTable;
